import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  Storage as StorageIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getCacheStats, clearAllCacheData, getCacheData } from '../../utils/cacheStorage.js';
import { parseCacheKey, getAllCsvCacheKeys } from '../../utils/cacheKeys.js';

export default function CacheManagement() {
  const [stats, setStats] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cacheEntries, setCacheEntries] = useState([]);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const refreshStats = () => {
    const currentStats = getCacheStats();
    setStats(currentStats);
    
    // Get detailed cache entries
    const keys = getAllCsvCacheKeys();
    const entries = keys.map(key => {
      const parsed = parseCacheKey(key);
      const data = getCacheData(key);
      return {
        key,
        dateStr: parsed?.dateStr || 'Unknown',
        storeId: parsed?.storeId || 'No Store',
        sectionsCount: data?.sections?.length || 0,
        timestamp: data?.timestamp || 0,
        size: JSON.stringify(data || {}).length
      };
    }).sort((a, b) => b.timestamp - a.timestamp); // Sort by most recent first
    
    setCacheEntries(entries);
  };

  useEffect(() => {
    refreshStats();
  }, []);

  const handleClearCache = () => {
    const cleared = clearAllCacheData();
    setClearConfirmOpen(false);
    refreshStats();
    
    // Show success message (you could use a snackbar here)
    console.log(`Cleared ${cleared} cache entries`);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString();
  };

  if (!stats) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading cache statistics...</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ padding: 3, marginBottom: 0 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'medium' }}>
        <StorageIcon />
        Cache Management
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>What is this cache?</strong>
        </Typography>
        <Typography variant="body2">
          This cache stores time-series data (sales, labor, operations) downloaded from your POS system for the <strong>/reports</strong> screen. 
          Cached data loads instantly instead of re-downloading from the server, making report generation much faster.
        </Typography>
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {stats.entryCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cache Entries
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {stats.totalSizeMB.toFixed(2)} MB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Storage Used
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.utilizationPercent} 
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {stats.utilizationPercent.toFixed(1)}% of {stats.maxSizeMB} MB limit
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {stats.totalAccesses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Accesses
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Chip 
                  label={stats.entryCount > 0 ? "Active" : "Empty"}
                  color={stats.entryCount > 0 ? "success" : "default"}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Cache Status
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {stats.utilizationPercent > 80 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Cache storage is {stats.utilizationPercent.toFixed(1)}% full. 
              Consider clearing old entries to free up space.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 0 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshStats}
        >
          Refresh Stats
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={() => setDetailsOpen(true)}
          disabled={stats.entryCount === 0}
        >
          View Details
        </Button>
        
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setClearConfirmOpen(true)}
          disabled={stats.entryCount === 0}
        >
          Clear Cache
        </Button>
      </Box>

      {/* Cache Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reports Data Cache Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {cacheEntries.length} cached reports data entries. Each entry contains time-series data 
            (sales, labor, operations) for a specific date and store combination.
          </Typography>
          
          <List>
            {cacheEntries.map((entry, index) => (
              <React.Fragment key={entry.key}>
                <ListItem>
                  <ListItemText
                    primary={`${entry.dateStr} - ${entry.storeId === null ? 'All Stores' : `Store ${entry.storeId}`}`}
                    secondary={
                      <span>
                        {entry.sectionsCount} sections • {formatBytes(entry.size)} • 
                        Cached: {formatDate(entry.timestamp)}
                      </span>
                    }
                  />
                </ListItem>
                {index < cacheEntries.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          
          {cacheEntries.length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              No cache entries found
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clear Cache Confirmation Dialog */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
      >
        <DialogTitle>Clear Reports Data Cache</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear all cached reports data? 
            This will remove {stats.entryCount} entries ({stats.totalSizeMB.toFixed(2)} MB) 
            and cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>What happens next:</strong> The next time you visit the /reports screen, 
            data will be re-downloaded from your POS system, which may take longer to load.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleClearCache} color="error" variant="contained">
            Clear Cache
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
